/**
 * Shared draft shape for the plan-aware wizard. Every step reads from and
 * writes to this object via the parent's setDraft callback. The draft is
 * autosaved to local_field_records (Dexie) between keystrokes and assembled
 * into a FieldRecordUpsert at submit.
 */
import type { SamplingPlanItem } from '@argos/api-types';
import type { Reading } from '../../lib/stabilization';

export interface SampleCollected {
  /** Cross-references the analyte_group on the plan item. */
  analyte_group: string | null;
  /** "40mL VOA", "1L Amber Glass", etc. */
  container: string;
  /** "HCl", "HNO3", "Cool 4C", or empty. */
  preservative?: string;
  /** Lab-assigned sample ID — optional; the tech can fill on-site if the lab has pre-printed labels. */
  lab_id?: string;
  /** True when the tech checked the box (container is in the cooler). */
  collected: boolean;
  /** True when the container was field-filtered. Defaults to the plan item's `filtered` flag. */
  filtered: boolean;
}

export interface WizardDraft {
  // Gauging
  is_dry: boolean;
  depth_to_water_ft: string;
  water_elevation_ft: string;
  product_thickness_ft: string;
  flag_code: string | null;

  // Field parameters (stabilization stream)
  field_param_readings: Reading[];

  // Purge
  purge_method: string;          // peristaltic | submersible | bailer | passive | ''
  purge_volume_l: string;
  pump_rate_lpm: string;

  // Sample collection — one row per (well, analyte_group, container instance).
  // Pre-populated when the wizard loads from the plan_item's containers list.
  samples_collected: SampleCollected[];

  // Observations
  weather: string;
  well_condition: string;
  odor: string;
  sheen: string;
  observations: string;
  notes: string;
}

export const EMPTY_DRAFT: WizardDraft = {
  is_dry: false,
  depth_to_water_ft: '',
  water_elevation_ft: '',
  product_thickness_ft: '',
  flag_code: null,
  field_param_readings: [],
  purge_method: '',
  purge_volume_l: '',
  pump_rate_lpm: '',
  samples_collected: [],
  weather: '',
  well_condition: '',
  odor: '',
  sheen: '',
  observations: '',
  notes: '',
};

/** Build the initial `samples_collected` list from a plan item's containers
 *  expanded by count (1×3 40mL VOA → 3 rows). Called once per well when the
 *  wizard mounts. */
export function expandPlanItemSamples(items: SamplingPlanItem[]): SampleCollected[] {
  const rows: SampleCollected[] = [];
  for (const it of items) {
    if (it.gauge_only) continue;
    const containers = ((it as unknown as { containers?: Array<{ type: string; count: number; preservative?: string }> }).containers) ?? [];
    for (const c of containers) {
      for (let i = 0; i < (c.count || 0); i++) {
        rows.push({
          analyte_group: it.analyte_group ?? null,
          container: c.type,
          preservative: c.preservative,
          collected: false,
          filtered: !!(it as unknown as { filtered?: boolean }).filtered,
        });
      }
    }
  }
  return rows;
}

export type StepId =
  | 'gauging'
  | 'field-params'
  | 'purge'
  | 'samples'
  | 'observations'
  | 'review';

/** Compute which steps apply to this well, given its plan items. Returns
 *  the ordered list of step IDs the wizard should walk. */
export function stepsForWell(items: SamplingPlanItem[]): StepId[] {
  // Treat the well as gauge-only when no items exist (rare, but possible
  // when a well was added as a placeholder) or every item flags gauge_only.
  const allGauge = items.length === 0 || items.every((i) => (i as unknown as { gauge_only?: boolean }).gauge_only);
  const needsFieldParams = items.some((i) => i.field_params || (i as unknown as { low_flow?: boolean }).low_flow);
  const needsPurge = items.some((i) => i.purge_required) && !allGauge;
  const hasSamples = !allGauge && items.some((i) => !(i as unknown as { gauge_only?: boolean }).gauge_only && i.analyte_group);

  const steps: StepId[] = ['gauging'];
  if (needsFieldParams) steps.push('field-params');
  if (needsPurge) steps.push('purge');
  if (hasSamples) steps.push('samples');
  steps.push('observations', 'review');
  return steps;
}

export function stepLabel(id: StepId): string {
  switch (id) {
    case 'gauging':       return 'Gauging';
    case 'field-params':  return 'Field params';
    case 'purge':         return 'Purge';
    case 'samples':       return 'Samples';
    case 'observations':  return 'Observations';
    case 'review':        return 'Review';
  }
}
